import React, { useState } from 'react';
import { Search } from 'lucide-react';

const ProjectSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const projectsPerPage = 9; // 3x3 grid
  let backendURL = process.env.REACT_APP_backend_url;
  const searchProjects = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${backendURL}/searchForProjectAbstract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const data = await response.json();
      // console.log(data)
      setResults(data);
      setTotalPages(Math.ceil(data.length / projectsPerPage));
      setCurrentPage(1); // Reset to first page on new search
    } catch (err) {
      setError('Failed to fetch results. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    searchProjects();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // Get current page projects
  const getCurrentProjects = () => {
    const indexOfLastProject = currentPage * projectsPerPage;
    const indexOfFirstProject = indexOfLastProject - projectsPerPage;
    // console.log(results.slice(indexOfFirstProject, indexOfLastProject),"print here...")
    return results.slice(indexOfFirstProject, indexOfLastProject);
  };

  // Function to create department badge with appropriate color
  const DepartmentBadge = ({ department }) => {
    const deptColors = {
      'IT': 'bg-blue-100 text-blue-800',
      'CS': 'bg-green-100 text-green-800',
      'ECE': 'bg-purple-100 text-purple-800',
      'ML': 'bg-red-100 text-red-800',
    };
    
    const color = department in deptColors ? deptColors[department] : 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        {department}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Search Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Project Search</h1>
        <div className="flex gap-2">
          <div className="relative flex-grow">
            <input
              type="text"
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search for projects by abstract content..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Search
          </button>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="flex justify-center my-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Results Grid */}
      {!loading && results.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {getCurrentProjects().map((result, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="p-3">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-gray-900 line-clamp-2">{result.project.ProjectTitle}</h3>
                    <span className="text-md font-medium text-gray-600">{result.project.Year|| 'N/A'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <DepartmentBadge department={result.project.Department} />
                    <span className="text-md text-gray-600">{result.project.Project_Type || 'Research'}</span>
                  </div>
                  
                  <div className="mb-3">
                    <span className="text-md font-medium text-gray-700">Faculty:</span>
                    <span className="text-md text-gray-600 ml-1">{result.project.ProjectSupervisor || 'Not specified'}</span>
                  </div>
                  
                  {result.project.AdditionalInfo.Abstract && (
                     <div className="mb-3">
                     <span className="text-md font-medium text-gray-700">Abstract:</span>
                    <p className="text-md text-gray-600  mb-3">{result.project.AdditionalInfo.Abstract}</p></div>
                  )}
                  
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-md text-gray-500">ID: {result.project.ProjectId || 'N/A'}</span>
                    <span className="text-lg bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                      Match: {(result.score * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div className="mt-8 flex justify-center">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &laquo;
              </button>
              
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &lsaquo;
              </button>
              
              {[...Array(totalPages).keys()].map(page => {
                const pageNumber = page + 1;
                // Show 5 pages at most - current, 2 before and 2 after when possible
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full ${
                        pageNumber === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber}>...</span>;
                }
                return null;
              })}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &rsaquo;
              </button>
              
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &raquo;
              </button>
            </div>
          </div>
          
          <div className="mt-2 text-center text-sm text-gray-600">
            Page {currentPage} of {totalPages} • Showing {getCurrentProjects().length} of {results.length} results
          </div>
        </>
      )}
      
      {/* Empty State */}
      {!loading && results.length === 0 && query && (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <Search size={48} />
          </div>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No results found</h3>
          <p className="mt-1 text-gray-500">Try adjusting your search terms or try a different query.</p>
        </div>
      )}
      
      {/* Initial State */}
      {!loading && results.length === 0 && !query && (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <Search size={48} />
          </div>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Start your search</h3>
          <p className="mt-1 text-gray-500">Enter keywords to find relevant research projects.</p>
        </div>
      )}
    </div>
  );
};

export default ProjectSearch;